import { translateCount } from "./plural";

// The guests of a search: adults and every child with its age. The counts
// the rest of the app needs are derived here, so the ages are never lost.

export const DEFAULT_ADULT_AGE = 18;

const childrenOf = (persons) => (Array.isArray(persons?.children) ? persons.children : []);

export const childCount = (persons) => childrenOf(persons).length;

export const childAges = (persons) => childrenOf(persons).map((child) => child.age);

export const totalGuests = (persons) => (Number(persons?.adults) || 0) + childCount(persons);

export const missingChildAges = (persons) =>
  childrenOf(persons).filter((child) => !Number.isInteger(child.age)).map((child) => child.id);

export const hasAllChildAges = (persons) => missingChildAges(persons).length === 0;

const positive = (value) => {
  const number = Number(value);

  return Number.isInteger(number) && number >= 0 && value !== null && value !== "" ? number : null;
};

// Limits of the property from its booking website settings; null means none.
export const guestLimits = (property) => {
  const settings = property?.iframe_settings ?? {};
  const adultAge = positive(settings.adult_age);

  return {
    maxAdults: positive(settings.max_adults),
    maxChildren: positive(settings.max_children),
    adultAge: adultAge && adultAge > 0 ? adultAge : DEFAULT_ADULT_AGE,
  };
};

// Query parameters of the availability requests.
export const searchParams = (persons) => ({
  adults: Number(persons?.adults) || 1,
  children: childCount(persons),
  children_ages: childAges(persons).filter((age) => Number.isInteger(age)),
});

export const appendSearchParams = (params, persons) => {
  const guests = searchParams(persons);
  params.set("adults", guests.adults);
  params.set("children", guests.children);
  params.delete("children_ages[]");
  guests.children_ages.forEach((age) => params.append("children_ages[]", age));

  return params;
};

// The guests of a reservation as the submit endpoint expects them.
export const reservationPersons = (persons) => ({
  adults: Number(persons?.adults) || 1,
  children: childCount(persons),
  ageOfChildren: childAges(persons),
  pets: Number(persons?.pets) || 0,
});

// "Add guests", "2 guests" or "2 adults, 2 children": children are named
// separately because their ages will matter for prices and occupancy.
export const guestSummary = (t, language, persons) => {
  if (!persons?.chosen) {
    return t("add_guests", "Add guests");
  }
  const adults = Number(persons.adults) || 1;
  const children = childCount(persons);
  if (children === 0) {
    return translateCount(t, language, "guests_count", adults);
  }

  return `${translateCount(t, language, "adults_count", adults)}, ${translateCount(
    t,
    language,
    "children_count",
    children
  )}`;
};
